// ============================================================================
// PsyMethodDetails — ВСТРОЕННАЯ карточка выбранной методики (не окно).
//
// Показывает описание, признаки шаблона, состояние назначения, результаты
// ИМЕННО этой методики и действия специалиста. Панель живёт в потоке страницы:
// на телефоне не перекрывает контент и не требует модального окна.
// ============================================================================

import { CheckCircle2, Clock3, FlaskConical, Play, Share2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import {
  METHOD_CATEGORY_HINT, METHOD_CATEGORY_LABEL, METHOD_SCORING_LABEL,
  questionWord, type PollMethodItem,
} from '@/components/polls/pollMethods';
import { activeLink, shortCode, type PatientResult, type PsyAssignment } from '@/lib/psyWorkspace';
import { PsyResultsHistory } from '@/components/psy/PsyResultsHistory';

export interface PsyMethodDetailsProps {
  method: PollMethodItem;
  assignment: PsyAssignment | null;
  results: PatientResult[];
  canManage: boolean;
  patientName?: string;
  onClose: () => void;
  onFill: () => void;
  onManual: () => void;
  onAssign: () => void;
}

export function PsyMethodDetails({
  method, assignment, results, canManage, patientName, onClose, onFill, onManual, onAssign,
}: PsyMethodDetailsProps) {
  const link = activeLink(assignment);
  const lastResult = results[0];
  const scoring = METHOD_SCORING_LABEL[String(method.scoring || '').toLowerCase()];

  return (
    <section className="rounded-lg border bg-card p-3 space-y-2.5" aria-label={`Методика ${method.synonym}`}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{method.synonym}</div>
          <div className="text-[10px] text-muted-foreground">
            {METHOD_CATEGORY_LABEL[method.category]} · {method.code}
          </div>
        </div>
        <Button size="sm" variant="ghost" className="h-7 w-7 shrink-0 p-0" onClick={onClose} title="Свернуть методику">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {method.description && (
        <div className="text-xs text-muted-foreground break-words">{method.description}</div>
      )}

      <div className="flex flex-wrap gap-1.5 text-[10px]">
        <span className="rounded-full border bg-muted/40 px-1.5 py-0.5">
          {method.questionCount === null
            ? 'вопросы не прочитаны'
            : method.questionCount === 0
              ? 'без вопросов · ручной результат'
              : `${method.questionCount} ${questionWord(method.questionCount)}`}
        </span>
        {scoring && <span className="rounded-full border bg-muted/40 px-1.5 py-0.5">{scoring}</span>}
        <span className="rounded-full border bg-muted/40 px-1.5 py-0.5" title={METHOD_CATEGORY_HINT[method.category]}>
          {METHOD_CATEGORY_LABEL[method.category]}
        </span>
      </div>

      {patientName && (
        <div className="text-[11px] text-muted-foreground">
          Пациент: <span className="text-foreground">{patientName}</span>
        </div>
      )}

      {assignment && (
        <div className="rounded-md border bg-muted/20 px-2.5 py-2 text-[11px] space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {String(assignment.status || '').toLowerCase() === 'assigned' ? (
              <Clock3 className="h-3.5 w-3.5 text-primary" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            )}
            <span>
              Назначение: {String(assignment.status || '').toLowerCase() === 'assigned' ? 'действует' : assignment.status || '—'}
            </span>
            {assignment.assignedDate && (
              <span className="text-muted-foreground">
                · {formatValue(assignment.assignedDate, '', 'date')}
              </span>
            )}
          </div>
          {link && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground">Код для пациента:</span>
              <span className="rounded border bg-background px-1.5 py-0.5 font-mono tracking-wider">
                {shortCode(link.token).toUpperCase()}
              </span>
              {link.expiresAt && (
                <span className="text-muted-foreground">
                  · до {new Date(link.expiresAt).toLocaleDateString('ru-RU')}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {lastResult && (
        <div className="text-[11px] text-muted-foreground">
          Последний результат: {lastResult.createdAt ? formatValue(lastResult.createdAt, '', 'timestamp') : '—'}
          {lastResult.score ? ` · балл: ${lastResult.score}` : ''}
          {lastResult.passed ? ' · порог пройден' : ''}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {!method.manualOnly && (
          <Button size="sm" className="h-8 text-[11px]" onClick={onFill}>
            <Play className="mr-1 h-3 w-3" />Пройти
          </Button>
        )}
        {method.manualOnly && canManage && (
          <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={onManual}>
            <FlaskConical className="mr-1 h-3 w-3" />Результат вручную
          </Button>
        )}
        {canManage && !method.manualOnly && (
          <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={onAssign}>
            <Share2 className="mr-1 h-3 w-3" />{link ? 'Ссылка и код' : 'Назначить'}
          </Button>
        )}
        {!canManage && (
          <span className="text-[11px] text-muted-foreground">
            Назначение и ручной результат вносит специалист.
          </span>
        )}
      </div>

      <div className="border-t pt-2">
        <PsyResultsHistory
          results={results}
          assignments={assignment ? [assignment] : undefined}
          methodName={() => method.synonym}
          methodCode={method.code}
          title="Результаты методики"
          emptyText="По этой методике результатов пока нет."
          pageSize={10}
        />
      </div>
    </section>
  );
}
